/* eslint-disable react-refresh/only-export-components -- o provedor e o hook de
   consumo moram juntos de propósito: separar `useCart` em outro arquivo só
   criaria um import a mais em toda tela que mexe no carrinho. */
import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import {
  cartReducer,
  ESTADO_INICIAL,
  quantidadeTotal as somarQuantidades,
} from '@/features/cart/cartReducer';
import { carregar, salvar } from '@/features/cart/cartStorage';
import type { ItemCarrinho } from '@/lib/pricing';

/**
 * Estado global do carrinho — F4.3.
 *
 * POR QUE CONTEXTO E NÃO ZUSTAND/REDUX: o estado global inteiro é um vetor de
 * 1 a 10 itens com slug e quantidade, mudado por meia dúzia de ações. Uma
 * dependência a mais custaria peso no pacote e mais um jeito de fazer as coisas
 * no repositório, sem resolver nenhum problema que o `useReducer` já não
 * resolva. Se um dia o carrinho virar estado de servidor, quem entra é o
 * `@tanstack/react-query`, que já está aqui — não um gerenciador de estado.
 */

export interface CarrinhoContexto {
  itens: ItemCarrinho[];
  unidadeSlug: string | null;
  cupom: string | null;
  /** Soma das quantidades — o número do contador no cabeçalho. */
  quantidadeTotal: number;
  adicionar(exameSlug: string, quantidade?: number): void;
  remover(exameSlug: string): void;
  definirQuantidade(exameSlug: string, q: number): void;
  definirUnidade(slug: string | null): void;
  definirCupom(c: string | null): void;
  limpar(): void;
  contem(exameSlug: string): boolean;
}

const Contexto = createContext<CarrinhoContexto | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [estado, dispatch] = useReducer(cartReducer, ESTADO_INICIAL);

  // Hidrata uma vez na montagem. Roda depois da pintura, então não atrasa o
  // primeiro quadro — o carrinho aparece vazio por um instante e se preenche.
  useEffect(() => {
    const salvo = carregar();
    if (salvo) dispatch({ type: 'hidratar', estado: salvo });
  }, []);

  // Persiste a cada mudança real.
  //
  // A guarda `versao > 0` é o ponto delicado: no primeiro commit o estado ainda
  // é o inicial vazio, e gravar aqui apagaria o carrinho salvo ANTES do efeito
  // de hidratação conseguir lê-lo. Só escrevemos depois que algo de fato mudou
  // — e a própria hidratação conta como mudança, o que renova a validade de 7
  // dias a cada visita (expiração deslizante, que é o que se espera de carrinho).
  useEffect(() => {
    if (estado.versao === 0) return;
    salvar(estado);
  }, [estado]);

  const valor = useMemo<CarrinhoContexto>(
    () => ({
      itens: estado.itens,
      unidadeSlug: estado.unidadeSlug,
      cupom: estado.cupom,
      quantidadeTotal: somarQuantidades(estado.itens),
      // `dispatch` é estável entre renderizações, então nenhuma destas funções
      // precisa entrar na lista de dependências.
      adicionar: (exameSlug, quantidade) => dispatch({ type: 'adicionar', exameSlug, quantidade }),
      remover: (exameSlug) => dispatch({ type: 'remover', exameSlug }),
      definirQuantidade: (exameSlug, q) =>
        dispatch({ type: 'definirQuantidade', exameSlug, quantidade: q }),
      definirUnidade: (slug) => dispatch({ type: 'definirUnidade', unidadeSlug: slug }),
      definirCupom: (c) => dispatch({ type: 'definirCupom', cupom: c }),
      limpar: () => dispatch({ type: 'limpar' }),
      contem: (exameSlug) => estado.itens.some((i) => i.exameSlug === exameSlug),
    }),
    [estado],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useCart(): CarrinhoContexto {
  const ctx = useContext(Contexto);
  if (!ctx) {
    throw new Error('useCart precisa estar dentro de <CartProvider>.');
  }
  return ctx;
}
